/* eslint-disable import/order */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useRef } from 'react';
import { arrayOf, bool, func, number, shape } from 'prop-types';
import { Box, useMediaQuery } from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import * as R from 'ramda';

import './selector/Selector.css';

// components
import Filters from '../../filters/Filters.connector';
import MobileFilters from '../../filters/MobileFilters';
import SelectorsList from '../../selectors-list/SelectorsList';
import SelectorVirtualizer from '../../selectors-list/SelectorVirtualizer';
import SelectorsSkeleton from './SelectorsSkeleton';
import Selector from './selector/Selector.connector';
import { NoAuthorizationsAvailable } from 'feature/loading-management/shared-components';

// styles
import selectorsListStyles from './Sidebar.styles';

// context
import { AuthContext } from 'authentication/AuthContext';

// Utils
import { sidebarStyles, VIEW_BY_OPTIONS } from '../../utils/const';

import { useSetSelectorIndex, useURLRouting, useUserRolePermissions } from 'hooks';
import { useTheme } from '@mui/material/styles';

const Sidebar = ({
  currentSelection,
  dataListPending,
  fetchSelfAuthorizations,
  fetchUniqueSelectors,
  resetCardFilters,
  selectedIndex,
  selectorsData,
  setActiveSelfAuthTab,
  setAuthorizationBy,
  setDataList,
  setFilters,
  setMobileDrawerOpen,
  setSelfAuthorizations,
  setSelfAuthorizationsPending,
}) => {
  const theme = useTheme();
  const { currentAffiliate } = useContext(AuthContext);

  const previousSelectorFetch = useRef();

  const { viewBy, viewById } = useParams();
  const { state } = useLocation();
  const referral = state?.referral;

  const lgUp = useMediaQuery(theme.breakpoints.up('lg'), { noSsr: true });
  const SELECTOR_SKELETON_HEIGHT = 157; // actual height of the rendered selector container
  const { customerIdName, dataCompanyId } = currentAffiliate ?? {};
  const { isSupplier, isInventoryOwner, isProductLoadingView } = useUserRolePermissions();

  // this will updated the currentSelection when the user hits the back button
  useURLRouting(currentSelection, selectorsData, viewById, setFilters);

  // This is to update our selector index based off of the URL
  useSetSelectorIndex(selectorsData, setFilters);

  useEffect(() => {
    setDataList([]);
    setSelfAuthorizations({});
    setAuthorizationBy(viewBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewBy]);

  // fetch location list for the current affiliate if currentAffiliate is changed
  useEffect(() => {
    const fetchData = () => {
      const affiliateParams = {
        affiliate: {
          companyCustomerIdName: customerIdName,
          dataCompanyId,
        },
      };
      previousSelectorFetch.current = null;
      fetchUniqueSelectors(affiliateParams, viewBy);
    };

    if (dataCompanyId) {
      setSelfAuthorizationsPending(true);
      fetchData();
    }
  }, [customerIdName, dataCompanyId, viewBy]);

  useEffect(() => {
    if (
      currentSelection.nameShort &&
      previousSelectorFetch.current !== currentSelection.nameShort
    ) {
      // initializing function inside useEffect so it doesn't need to be garbage collected
      const updateTitleAndTab = () => {
        if (viewBy === 'location') {
          if (isInventoryOwner || isProductLoadingView) {
            setActiveSelfAuthTab('baseProducts');
          } else {
            setActiveSelfAuthTab('supplierDirectedBlends');
          }
        }
        if (viewBy === 'product') {
          if (R.isNil(currentSelection.proprietaryProduct)) {
            setActiveSelfAuthTab('baseProducts');
          } else if (!isInventoryOwner || !isProductLoadingView) {
            setActiveSelfAuthTab('supplierDirectedBlends');
          } else {
            setActiveSelfAuthTab('customProducts');
          }
        }
      };

      previousSelectorFetch.current = currentSelection.nameShort;
      const queryParams = () => {
        const base = {
          Affiliate: {
            CompanyCustomerIdName: currentAffiliate.customerIdName,
            DataCompanyId: currentAffiliate.dataCompanyId,
          },
          [`${viewBy}Id`]: currentSelection.id,
        };
        if (viewBy === 'product') {
          base.productTypes = R.isNil(currentSelection.proprietaryProduct)
            ? 'BaseProductsOnly'
            : 'BlendsOnly';
        }
        return base;
      };
      fetchSelfAuthorizations(queryParams());

      // This is done here so that the logic will be evaluated as the fetch is being made
      // We need to tab to be correct once the fetch resolves to keep from showing NoAuthAvailable card
      !referral && updateTitleAndTab();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelection.nameShort]);

  useEffect(() => {
    // If there is no selectors in the left hand side
    // after fetching then set self auth pending to false
    if (R.isEmpty(selectorsData) && !dataListPending) {
      setSelfAuthorizationsPending(false);
    }
  }, [selectorsData, dataListPending]);

  const handleSelectorClick = idx => {
    if (idx !== selectedIndex) {
      setFilters({ name: 'selectedIndex', value: idx });
    }
    if (!lgUp) {
      setMobileDrawerOpen(true);
    }
    resetCardFilters();
  };

  const getProductSize = index => {
    const isBaseProduct = R.isNil(selectorsData[index].proprietaryProduct);
    let height = 113;
    // if base product and inventory owner
    if (isBaseProduct && (isInventoryOwner || isProductLoadingView)) {
      height = 85;
    }
    // if supplier
    if (isSupplier && !isInventoryOwner && !isBaseProduct) {
      height = 85;
    }
    // if !base product, inventory owner, !supplier
    if (!isBaseProduct && isInventoryOwner && !isSupplier) {
      height = 101;
    }
    return height;
  };

  const getLocationSize = () => {
    let height = 141;
    if (isInventoryOwner && !isSupplier) {
      height = 113;
    }
    if (!isInventoryOwner && isSupplier) {
      height = 85;
    }
    return height;
  };

  return (
    <Box {...sidebarStyles}>
      {lgUp ? (
        <Filters loading={dataListPending} viewByOptions={VIEW_BY_OPTIONS} />
      ) : (
        <MobileFilters>
          <Filters loading={dataListPending} viewByOptions={VIEW_BY_OPTIONS} />
        </MobileFilters>
      )}
      {!lgUp && R.isEmpty(selectorsData) && !dataListPending ? (
        <Box p={3}>
          <NoAuthorizationsAvailable />
        </Box>
      ) : (
        <SelectorsList
          loading={dataListPending}
          selector={
            <SelectorVirtualizer
              handleSelectorClick={handleSelectorClick}
              itemSize={viewBy === 'product' ? getProductSize : getLocationSize}
              overscanCount={4}
              selectorComponent={Selector}
              selectorsData={selectorsData}
              selectorStyles={() => {}}
            />
          }
          skeletonComponent={<SelectorsSkeleton skeletonHeight={SELECTOR_SKELETON_HEIGHT} />}
          skeletonHeight={SELECTOR_SKELETON_HEIGHT}
          sx={{ ...selectorsListStyles(theme) }}
        />
      )}
    </Box>
  );
};

export default Sidebar;

Sidebar.propTypes = {
  currentSelection: shape({}),
  dataListPending: bool.isRequired,
  fetchSelfAuthorizations: func.isRequired,
  fetchUniqueSelectors: func.isRequired,
  resetCardFilters: func.isRequired,
  selectedIndex: number,
  selectorsData: arrayOf(shape({})),
  setActiveSelfAuthTab: func.isRequired,
  setAuthorizationBy: func.isRequired,
  setDataList: func.isRequired,
  setFilters: func.isRequired,
  setMobileDrawerOpen: func.isRequired,
  setSelfAuthorizations: func,
  setSelfAuthorizationsPending: func.isRequired,
};

Sidebar.defaultProps = {
  currentSelection: {},
  selectedIndex: null,
  selectorsData: [],
  setSelfAuthorizations: () => {},
};
